import axios, {
    AxiosResponse,
    AxiosError,
    InternalAxiosRequestConfig,
} from 'axios';
import { env } from '../config/env';
import { useAuthStore } from '../store/authStore';
import { handleApiError } from './errorHandler';
import { toastError } from '@components/common/Toast/CustomToast';
import { loadingManager } from '@/contexts/loadingManager';

// 커스텀 설정 인터페이스
interface CustomAxiosConfig extends InternalAxiosRequestConfig {
    skipLoading?: boolean; // 로딩 스키핑 옵션
}

// Axios 인스턴스 생성
const apiClient = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
apiClient.interceptors.request.use(
    (config: CustomAxiosConfig) => {
        // skipLoading이 true가 아닌 경우에만 로딩 시작
        if (!config.skipLoading) {
            loadingManager.start();
        }

        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    error => {
        loadingManager.stop(); // 에러시에는 항상 로딩 끄기
        return Promise.reject(error);
    },
);

// Response interceptor
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        const config = response.config as CustomAxiosConfig;
        // skipLoading이 true가 아닌 경우에만 로딩 끄기
        if (!config.skipLoading) {
            loadingManager.stop();
        }

        if (response.data.code !== 0) {
            toastError(response.data.message, response.data.messageDetail);
            return Promise.reject(response.data.message);
        }
        return response;
    },
    (error: AxiosError) => {
        loadingManager.stop(); // 에러시에는 항상 로딩 끄기

        if (error.response?.status === 401) {
            const { logout } = useAuthStore.getState();
            logout();
            window.location.href = '/login';
        }
        handleApiError(error);
        return Promise.reject(error);
    },
);

export default apiClient;
